import { api_url } from '../../utils/Enviroment';
import { io } from 'socket.io-client';

const BASE = `${api_url}/generate-market`;
const TIMEOUT = 15000;

// ─────────────────────────────────────────────
// Simple Fetch Wrapper
// ─────────────────────────────────────────────
class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(path, options = {}, timeout = TIMEOUT) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      signal: controller.signal,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(data.message || `Error ${res.status}`, res.status);
    }

    return res.json();
  } finally {
    clearTimeout(id);
  }
}

// ─────────────────────────────────────────────
// Socket (simple version)
// ─────────────────────────────────────────────
let socket;

function getSocket() {
  if (!socket) {
    socket = io(api_url, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
    });
  }
  return socket;
}

// ─────────────────────────────────────────────
// Light Normalizers (ONLY where useful)
// ─────────────────────────────────────────────
function normalizeClassification(raw) {
  if (!raw) return { classification: 'clear', confidence: 1, resolutionMethod: 'manual' };

  // Unwrap {data: {...}} envelope if present
  const d = raw?.data || raw;

  if (d.restricted) return { classification: 'restricted', confidence: 1 };

  if (d.hasSpellingError || d.classification === 'spelling') return {
    classification: 'spelling',
    confidence: d.confidence ?? 1,
    correctedQuestion: d.correctedQuestion || null,
    corrections: d.corrections || [],
    resolutionMethod: d.resolutionMethod || 'manual',
  };

  if (d.hasSemanticError || d.classification === 'semantic') return {
    classification: 'semantic',
    confidence: d.confidence ?? 1,
    correctedQuestion: d.correctedQuestion || null,
    corrections: d.corrections || [],
    resolutionMethod: d.resolutionMethod || 'manual',
  };

  if (d.classification && ['non-specific', 'unresolvable'].includes(d.classification)) {
    return {
      classification: d.classification,
      confidence: d.confidence ?? 1,
      resolutionMethod: d.resolutionMethod || 'manual',
    };
  }

  return {
    classification: 'clear',
    confidence: d.confidence ?? 1,
    category: d.category ?? 'Other',
    resolutionMethod: d.resolutionMethod || 'ai',
  };
}

function normalizeMarket(raw) {
  // Unwrap {data: {...}} envelope if present
  const data = raw?.data || raw;

  const outcomes = (data?.outcomes || []).map((o, i) => ({
    id: o.id || String(i + 1),
    label: o.label || `Option ${i + 1}`,
    probability: Math.round((o.probability ?? 50) * (o.probability <= 1 ? 100 : 1)),
  }));

  return {
    description: data?.description || '',
    outcomes,
    categories: data?.categories || [],
    resolutionDate: data?.resolutionDate || null,
    resolutionMethod: data?.resolutionMethod || 'yes-no',
  };
}

// ─────────────────────────────────────────────
// API
// ─────────────────────────────────────────────
export const api = {
  // 1. Spelling
  checkSpelling: (text, signal) =>
    request('/check-spelling', {
      method: 'POST',
      body: JSON.stringify({ text }),
      signal,
    }),

  // 2. Classification
  classifyQuestion: async (question) => {
    const raw = await request('/classify-question', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
    return normalizeClassification(raw);
  },

  // 3. Autocomplete (REST)
  autocomplete: (input, signal) =>
    request('/autocomplete', {
      method: 'POST',
      body: JSON.stringify({ question: input }),
      signal,
    }),

  // 3b. Autocomplete (Socket Stream)
  autocompleteStream: (input, onData, signal) => {
    const socket = getSocket();
    const id = Date.now();

    return new Promise((resolve) => {
      const results = [];

      const onSuggestion = (data) => {
        results.push(data);
        onData?.(data);
      };

      const onDone = () => {
        cleanup();
        resolve({ suggestions: results });
      };

      const cleanup = () => {
        socket.off(`autocomplete:suggestion/${id}`, onSuggestion);
        socket.off(`autocomplete:done/${id}`, onDone);
      };

      socket.on(`autocomplete:suggestion/${id}`, onSuggestion);
      socket.on(`autocomplete:done/${id}`, onDone);

      socket.emit('autocomplete', { question: input, requestId: id });

      // Abort support
      signal?.addEventListener(
        'abort',
        () => {
          cleanup();
          resolve({ suggestions: results });
        },
        { once: true }
      );

      // Fallback timeout
      setTimeout(() => {
        cleanup();
        resolve({ suggestions: results });
      }, 20000);
    });
  },

  // 4. Refine Question
  refineQuestion: (data) =>
    request('/refine-question', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 5. Generate Market
  generateMarket: async (question, resolutionMethod) => {
    const raw = await request(
      '/get-market',
      {
        method: 'POST',
        body: JSON.stringify({ question, resolutionMethod }),
      },
      25000
    );
    return normalizeMarket(raw);
  },

  // 6. Deploy
  deployToRain: (session, market) =>
    request(
      '/markets/deploy-to-rain',
      {
        method: 'POST',
        body: JSON.stringify({ session, market }),
      },
      60000
    ),

  // Aliases
  processQuestion: (q) => api.classifyQuestion(q),
  deployMarket: (m) => api.deployToRain(null, m),
};

export { ApiError, getSocket };

export const RESTRICTED_PATTERNS = [
  /(assassinat|murder|kill|shoot|attack|bomb|terror|stabbing|mass shooting)/i,
  /(פיגוע|רצח|חיסול|ירי|טרור|התנקשות)/i,
  /(suicide|self[- ]?harm|kill (him|her|them)self|commit suicide|end (his|her|their) life)/i,
  /(התאבדות|פגיעה עצמית)/i,
  /(hack|hacking|rob|robbery|steal|heist|drug deal|smuggl)/i,
  /(פריצה|שוד|גניבה|הברחה)/i,
  /(pump and dump|rug ?pull|will .{0,10}(pump|dump|collapse|crash) (tomorrow|tonight|today|next))/i,
];